/**
 * assets/customizer-gift-bag.js
 * Gift Bag-specific customization logic.
 */
class GiftBagCustomizer extends BaseCustomizer {
  constructor(config) {
    super(config);
    window.selectedGiftBagStyle = '';
    window.selectedGiftBagSize = '';

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.updateColorVariants());
    } else {
      setTimeout(() => this.updateColorVariants(), 0);
    }
  }

  get selectedGiftBagStyle() { return window.selectedGiftBagStyle; }
  set selectedGiftBagStyle(val) { window.selectedGiftBagStyle = val; }

  get selectedGiftBagSize() { return window.selectedGiftBagSize; }
  set selectedGiftBagSize(val) { window.selectedGiftBagSize = val; }

  bindGlobalMethods() {
    super.bindGlobalMethods();
    window.getGiftBagOptions = () => this.getGiftBagOptions();
    window.renderGiftBagPicker = () => this.renderGiftBagPicker();
    window.selectGiftBagStyle = (style) => this.selectGiftBagStyle(style);
    window.selectGiftBagBaseStyle = (baseStyle) => this.selectGiftBagBaseStyle(baseStyle);
    window.selectGiftBagSize = (size) => this.selectGiftBagSize(size);
    window.onGiftBagPackChange = (el) => this.onGiftBagPackChange(el);
    window.syncGiftBagVariant = () => this.syncGiftBagVariant();
    window.updateGiftBagVariants = () => this.updateGiftBagVariants();
    window.openGiftBagSetModal = (gKey) => this.openGiftBagSetModal(gKey);
  }

  updateGiftBagVariants() {
    this.updateColorVariants();
  }

  isPackStyle(style) {
    if (!style) return false;
    return /set of \d+|\d+\s*pack|bundle/i.test(style);
  }

  getBaseStyle(style) {
    if (!style) return '';
    if (style.toLowerCase().includes('physical bag') || style.toLowerCase().includes('gift bag')) {
      return 'Physical Bag';
    }
    if (style.toLowerCase().includes('dtf transfer sheet') || style.toLowerCase().includes('dtf transfer') || style.toLowerCase() === 'dtf') {
      return 'DTF Transfer Sheet';
    }
    const cleaned = style.split(/ - Set of/i)[0].trim();
    return cleaned || style;
  }

  getGiftBagOptions() {
    const allStyles = new Set();
    const sizes = new Set();
    
    window.productVariants.forEach(v => {
      const parsed = window.parseVariantTitle(v.title);
      if (parsed.style) allStyles.add(parsed.style);
      if (parsed.size) sizes.add(parsed.size);
    });

    const stylesArr = Array.from(allStyles);
    const baseStyles = [];
    const packStyles = [];

    stylesArr.forEach(style => {
      if (this.isPackStyle(style)) {
        packStyles.push(style);
      } else {
        baseStyles.push(style);
      }
    });

    if (baseStyles.length === 0 && stylesArr.length > 0) {
      stylesArr.forEach(style => {
        const base = this.getBaseStyle(style);
        if (base && !baseStyles.includes(base)) {
          baseStyles.push(base);
        }
      });
    }

    return {
      allStyles: stylesArr,
      baseStyles,
      packStyles,
      sizes: Array.from(sizes)
    };
  }

  renderGiftBagPicker() {
    const container = document.querySelector('.gift-bag-variant-selectors');
    if (!container) return;
    
    const { allStyles, baseStyles, packStyles } = this.getGiftBagOptions();

    if (!this.selectedGiftBagStyle && allStyles.length > 0) {
      this.selectedGiftBagStyle = baseStyles.length > 0 ? baseStyles[0] : allStyles[0];
    }

    const currentBaseStyle = this.getBaseStyle(this.selectedGiftBagStyle);

    // Filter available variants for the currently selected style
    const availableVariants = window.productVariants.filter(v => {
      const parsed = window.parseVariantTitle(v.title);
      return parsed.style === this.selectedGiftBagStyle;
    });

    const activeSizes = Array.from(new Set(availableVariants.map(v => window.parseVariantTitle(v.title).size).filter(Boolean)));

    if (activeSizes.length > 0 && !activeSizes.includes(this.selectedGiftBagSize)) {
      this.selectedGiftBagSize = activeSizes[0];
    }

    // 1. Render Base Style Buttons (e.g. "Physical Bag", "DTF Transfer Sheet")
    const styleContainer = document.getElementById('gift-bag-style-buttons');
    if (styleContainer) {
      const isPackSelected = this.isPackStyle(this.selectedGiftBagStyle);
      const styleBasis = baseStyles.length === 1 ? '100%' : (baseStyles.length === 2 ? 'calc(50% - 6px)' : 'calc(33.33% - 6px)');
      
      const buttonsHtml = baseStyles.map(baseStyle => {
        const isActive = !isPackSelected && currentBaseStyle === baseStyle;
        
        const styleVariants = window.productVariants.filter(v => {
          const parsed = window.parseVariantTitle(v.title);
          return parsed.style === baseStyle || (!this.isPackStyle(parsed.style) && this.getBaseStyle(parsed.style) === baseStyle);
        });
        
        const isStyleSoldOut = styleVariants.length > 0 && styleVariants.every(v => !v.available);
        
        let priceStr = '';
        if (styleVariants.length > 0) {
          const availablePrices = styleVariants.filter(v => v.available).map(v => v.price);
          const prices = availablePrices.length > 0 ? availablePrices : styleVariants.map(v => v.price);
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);
          if (minPrice === maxPrice) {
            priceStr = window.formatMoney(minPrice);
          } else {
            priceStr = `${window.formatMoney(minPrice)} - ${window.formatMoney(maxPrice)}`;
          }
        }
        
        const subtitle = isStyleSoldOut ? 'Sold Out' : priceStr;
        
        return `
          <button type="button" class="variant-option__button-label ${isActive ? 'active-style' : ''} ${isStyleSoldOut ? 'sold-out-slashed' : ''}" 
                  onclick="window.selectGiftBagBaseStyle('${baseStyle}')"
                  style="flex: 1 1 ${styleBasis}; min-width: calc(50% - 6px); padding: 12px 6px; min-height: 64px; border: 1px solid ${isActive ? 'var(--color-foreground)' : '#e5e7eb'}; border-radius: var(--style-border-radius-buttons-primary, 40px); background-color: ${isActive ? 'rgb(var(--color-foreground-rgb))' : '#fff'}; color: ${isActive ? 'var(--color-background)' : 'var(--color-foreground)'}; cursor: pointer; transition: all 0.2s; text-align: center; font-size: 0.95rem; font-weight: 500;">
            <span class="variant-option__button-title" style="display: block; font-weight: 500; font-size: 0.95rem; line-height: 1.2;">${baseStyle}</span>
            <span class="variant-option__button-price" style="font-size: 0.85rem; font-weight: 600; opacity: 0.75; display: block; margin-top: 4px;">${subtitle}</span>
          </button>
        `;
      }).join('');

      // 2. Render Dropdown for Pack Options (if any exist for current base style)
      const relevantPackStyles = packStyles.filter(ps => this.getBaseStyle(ps) === currentBaseStyle);
      
      let dropdownHtml = '';
      if (relevantPackStyles.length > 0) {
        const dropdownLabel = 'Buy More & Save...';
        
        dropdownHtml = `
          <div class="variant-option__dropdown-wrapper">
            <select id="gift-bag-pack-dropdown" 
                    class="variant-option__dropdown ${isPackSelected ? 'is-selected' : ''}"
                    onchange="window.onGiftBagPackChange(this)">
              <option value="${currentBaseStyle}" ${!isPackSelected ? 'selected' : ''}>${dropdownLabel}</option>
              ${relevantPackStyles.map(packStyle => {
                const isSelected = this.selectedGiftBagStyle === packStyle;
                
                const packVariants = window.productVariants.filter(v => window.parseVariantTitle(v.title).style === packStyle);
                const sizeMatch = packVariants.find(v => window.parseVariantTitle(v.title).size === this.selectedGiftBagSize);
                
                let priceStr = '';
                let isSoldOut = false;
                if (sizeMatch) {
                  priceStr = window.formatMoney(sizeMatch.price);
                  isSoldOut = !sizeMatch.available;
                } else if (packVariants.length > 0) {
                  const prices = packVariants.map(v => v.price);
                  const minPrice = Math.min(...prices);
                  const maxPrice = Math.max(...prices);
                  priceStr = minPrice === maxPrice ? window.formatMoney(minPrice) : `${window.formatMoney(minPrice)} - ${window.formatMoney(maxPrice)}`;
                  isSoldOut = packVariants.every(v => !v.available);
                }

                const cleanLabel = packStyle;

                return `<option value="${packStyle}" ${isSelected ? 'selected' : ''} ${isSoldOut ? 'disabled' : ''}>${cleanLabel}${priceStr ? ' (' + priceStr + ')' : ''}${isSoldOut ? ' - Sold Out' : ''}</option>`;
              }).join('')}
            </select>
          </div>
        `;
      }

      styleContainer.innerHTML = `
        <div style="display: flex; flex-wrap: wrap; gap: 10px; width: 100%;">
          ${buttonsHtml}
        </div>
        ${dropdownHtml}
      `;
    }
    
    // 3. Render Size Buttons (Show/Hide dynamically based on whether the selected style has sizes)
    const sizeFieldset = container.querySelector('.gift-bag-size-option');
    if (sizeFieldset) {
      const isDTF = currentBaseStyle.toLowerCase().includes('dtf');
      const hasSizes = activeSizes.length > 0 && !isDTF;
      sizeFieldset.style.display = hasSizes ? '' : 'none';
      
      if (hasSizes) {
        const sizeContainer = document.getElementById('gift-bag-size-buttons');
        if (sizeContainer) {
          const sizeBasis = activeSizes.length === 1 
            ? '100%' 
            : (activeSizes.length === 2 ? 'calc(50% - 6px)' : 'calc(33.33% - 6px)');

          sizeContainer.innerHTML = activeSizes.map(size => {
            const isActive = this.selectedGiftBagSize === size;
            
            const matchVar = availableVariants.find(v => {
              const parsed = window.parseVariantTitle(v.title);
              return parsed.size === size;
            });
            const isSizeSoldOut = matchVar ? !matchVar.available : true;
            const priceStr = matchVar ? window.formatMoney(matchVar.price) : '';
            const subtitle = isSizeSoldOut ? 'Sold Out' : priceStr;
            
            return `
              <button type="button" class="variant-option__button-label ${isActive ? 'active-style' : ''} ${isSizeSoldOut ? 'sold-out-slashed' : ''}" 
                      onclick="window.selectGiftBagSize('${size}')"
                      style="flex: 1 1 ${sizeBasis}; min-width: calc(33.33% - 6px); padding: 12px 6px; min-height: 64px; border: 1px solid ${isActive ? 'var(--color-foreground)' : '#e5e7eb'}; border-radius: var(--style-border-radius-buttons-primary, 40px); background-color: ${isActive ? 'rgb(var(--color-foreground-rgb))' : '#fff'}; color: ${isActive ? 'var(--color-background)' : 'var(--color-foreground)'}; cursor: pointer; transition: all 0.2s; text-align: center; font-size: 0.95rem; font-weight: 500;">
                <span class="variant-option__button-title" style="display: block; font-weight: 500; font-size: 0.95rem; line-height: 1.2;">${size}</span>
                ${subtitle ? `<span class="variant-option__button-price" style="font-size: 0.85rem; font-weight: 600; opacity: 0.75; display: block; margin-top: 4px;">${subtitle}</span>` : ''}
              </button>
            `;
          }).join('');
        }
      }
    }
  }

  selectGiftBagBaseStyle(baseStyle) {
    this.selectedGiftBagStyle = baseStyle;

    const availableVariants = window.productVariants.filter(v => {
      const parsed = window.parseVariantTitle(v.title);
      return parsed.style === this.selectedGiftBagStyle;
    });
    const activeSizes = Array.from(new Set(availableVariants.map(v => window.parseVariantTitle(v.title).size).filter(Boolean)));
    if (activeSizes.length > 0 && !activeSizes.includes(this.selectedGiftBagSize)) {
      this.selectedGiftBagSize = activeSizes[0];
    } else if (activeSizes.length === 0) {
      this.selectedGiftBagSize = '';
    }

    this.syncGiftBagVariant();
    this.renderGiftBagPicker();
  }

  selectGiftBagStyle(style) {
    this.selectedGiftBagStyle = style;

    // Check available sizes for the newly selected style
    const availableVariants = window.productVariants.filter(v => {
      const parsed = window.parseVariantTitle(v.title);
      return parsed.style === this.selectedGiftBagStyle;
    });
    const activeSizes = Array.from(new Set(availableVariants.map(v => window.parseVariantTitle(v.title).size).filter(Boolean)));
    if (activeSizes.length > 0 && !activeSizes.includes(this.selectedGiftBagSize)) {
      this.selectedGiftBagSize = activeSizes[0];
    } else if (activeSizes.length === 0) {
      this.selectedGiftBagSize = '';
    }

    this.syncGiftBagVariant();
    this.renderGiftBagPicker();
  }

  onGiftBagPackChange(selectElement) {
    const val = selectElement.value;
    if (val) {
      this.selectGiftBagStyle(val);
    }
  }

  selectGiftBagSize(size) {
    this.selectedGiftBagSize = size;
    this.syncGiftBagVariant();
    this.renderGiftBagPicker();
  }

  syncGiftBagVariant() {
    const isDigital = this.selectedGiftBagStyle && this.selectedGiftBagStyle.toLowerCase().includes('digital');
    const isDTF = this.selectedGiftBagStyle && this.selectedGiftBagStyle.toLowerCase().includes('dtf');
    
    const matchVar = window.productVariants.find(v => {
      const parsed = window.parseVariantTitle(v.title);
      if (parsed.style !== this.selectedGiftBagStyle) return false;
      if (isDigital || isDTF || !this.selectedGiftBagSize) return true;
      return parsed.size === this.selectedGiftBagSize;
    }) || window.productVariants.find(v => {
      return window.parseVariantTitle(v.title).style === this.selectedGiftBagStyle;
    }) || window.productVariants[0];
    
    if (matchVar) {
      const parsed = window.parseVariantTitle(matchVar.title);
      if (parsed.size && parsed.size !== this.selectedGiftBagSize) {
        this.selectedGiftBagSize = parsed.size;
      }
      
      const hiddenInput = document.getElementById('gift-bag-hidden-variant-input');
      if (hiddenInput) {
        hiddenInput.value = matchVar.id;
      }
      
      const mainProductForm = document.querySelector('form[action$="/cart/add"]');
      if (mainProductForm) {
        const mainVariantInput = mainProductForm.querySelector('input[name="id"]');
        if (mainVariantInput) {
          mainVariantInput.value = matchVar.id;
          mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', matchVar.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        console.warn("Could not update variant URL param", e);
      }
      
      const customization = this.allCustomizations[this.selectedCustomizationIndex];
      if (customization) {
        let gKey = customization.key;
        if (gKey) {
          const lastDashIndex = gKey.lastIndexOf('-');
          if (lastDashIndex !== -1) {
            gKey = gKey.substring(0, lastDashIndex);
          }
          
          // Update all items in this design set locally
          this.allCustomizations.forEach((item) => {
            if (item.key === gKey || (item.key && item.key.startsWith(gKey + '-'))) {
              item.variantId = matchVar.id;
            }
          });
        }
        customization.variantId = matchVar.id;
        
        if (this.updateRemoteHistoryItem && gKey) {
          this.updateRemoteHistoryItem(gKey, { variantId: matchVar.id });
        }
      }
      
      if (window.updateLivePrice) window.updateLivePrice();
    }
  }

  updateColorVariants() {
    const customization = this.allCustomizations[this.selectedCustomizationIndex];
    let targetId = customization ? customization.variantId : null;
    
    // If current item doesn't have a variantId yet, check if any peer in the same design set has one
    if (!targetId && customization && customization.key) {
      let gKey = customization.key;
      const lastDashIndex = gKey.lastIndexOf('-');
      if (lastDashIndex !== -1) {
        gKey = gKey.substring(0, lastDashIndex);
      }
      const peer = this.allCustomizations.find(item => item.key && (item.key === gKey || item.key.startsWith(gKey + '-')) && item.variantId);
      if (peer) {
        targetId = peer.variantId;
      }
    }
    
    if (targetId) {
      const activeVar = window.productVariants.find(v => String(v.id) === String(targetId));
      if (activeVar) {
        const parsed = window.parseVariantTitle(activeVar.title);
        this.selectedGiftBagStyle = parsed.style || '';
        this.selectedGiftBagSize = parsed.size || '';
      }
    } else if (!this.selectedGiftBagStyle) {
      const defaultVar = (window.productVariants && window.productVariants[0]) ? window.productVariants[0] : null;
      if (defaultVar) {
        const parsed = window.parseVariantTitle(defaultVar.title);
        this.selectedGiftBagStyle = parsed.style || '';
        this.selectedGiftBagSize = parsed.size || '';
      }
    }
    
    this.renderGiftBagPicker();
    this.syncGiftBagVariant();
    if (window.resetVariantVisibility) {
      window.resetVariantVisibility();
    }
  }

  openGiftBagSetModal(gKey) {
    const modal = document.getElementById('clothes-color-modal');
    if (!modal) return;

    const groupItems = this.allCustomizations.filter(item => 
      item.key && (item.key === gKey || item.key.startsWith(gKey + '-'))
    );
    if (groupItems.length === 0) return;

    let initialIdx = 0;
    if (this.selectedCustomizationIndex >= 0) {
      const activeItem = this.allCustomizations[this.selectedCustomizationIndex];
      if (activeItem) {
        const matchIdx = groupItems.findIndex(item => item.key === activeItem.key);
        if (matchIdx !== -1) initialIdx = matchIdx;
      }
    }

    const mainContainer = document.getElementById('modal-color-preview-container');
    const thumbContainer = document.getElementById('modal-color-thumbnails-strip');
    const colorContainer = document.getElementById('color-options-container');

    if (colorContainer) {
      colorContainer.style.display = 'none';
    }

    const selectModalPreview = (idx) => {
      const item = groupItems[idx];
      if (!item) return;

      const globalIdx = this.allCustomizations.findIndex(c => c.key === item.key);
      if (globalIdx !== -1) {
        this.selectedCustomizationIndex = globalIdx;
        const radio = document.querySelector(`.custom-item-card[data-index="${globalIdx}"] input[name="customization-selection"]`);
        if (radio) radio.checked = true;
        if (window.updateBuyButtonVisibility) window.updateBuyButtonVisibility();
      }

      let previewUrl = item.imageUrl || '';
      if (previewUrl.includes('/image/preview/')) {
        previewUrl = previewUrl.replace('/image/preview/', '/gift-bag/preview/');
      }

      if (mainContainer) {
        mainContainer.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; background: #f7f7f7; border-radius: 12px; padding: 12px; box-sizing: border-box;">
            <img src="${previewUrl}" alt="Generated Image" style="max-width: 100%; max-height: 40vh; width: auto; height: auto; object-fit: contain; display: block; cursor: zoom-in;" onclick="window.openImageZoom('${previewUrl}')" onerror="window.handleImageError(this)">
          </div>
        `;
      }

      if (thumbContainer) {
        const thumbs = thumbContainer.querySelectorAll('.result-thumb-item');
        thumbs.forEach((t, i) => {
          if (i === idx) t.classList.add('active');
          else t.classList.remove('active');
        });
      }
    };

    window.selectModalColorPreview = selectModalPreview;

    if (thumbContainer) {
      if (groupItems.length > 1) {
        thumbContainer.style.display = 'flex';
        thumbContainer.innerHTML = groupItems.map((item, idx) => {
          let thumbUrl = item.imageUrl || '';
          if (thumbUrl.includes('/image/preview/')) {
            thumbUrl = thumbUrl.replace('/image/preview/', '/gift-bag/preview/');
          }
          return `
            <div class="result-thumb-item ${idx === initialIdx ? 'active' : ''}" onclick="window.selectModalColorPreview(${idx})" style="flex: 0 0 60px; width: 60px; height: 60px; border-radius: 8px; border: 2px solid transparent; overflow: hidden; cursor: pointer; transition: all 0.2s; background: #f7f7f7; display: flex; align-items: center; justify-content: center; padding: 4px; box-sizing: border-box;">
              <img src="${thumbUrl}" style="width: 100%; height: 100%; object-fit: contain; display: block;">
            </div>
          `;
        }).join('');
      } else {
        thumbContainer.style.display = 'none';
      }
    }

    selectModalPreview(initialIdx);
    modal.style.display = 'flex';
    this.lockScreen();
  }
}
